import { createReducer, on } from '@ngrx/store';
import { CrmState } from '../../shared/models';
import * as CrmActions from './crm.actions';

const initialState: CrmState = {
  contacts: [],
  matters: [],
  timeline: [],
  billingEvents: [],
  billingTotal: 0,
  nextTimelineCursor: null,
  lastCreatedContactId: null,
  lastCreatedMatterId: null,
  documents: [],
  currentContact: null,
  currentMatter: null,
  uploadProgress: {
    isLoading: false,
    percentage: 0,
    filename: '',
    error: null,
    success: null,
  },
  isLoading: false,
  error: null,
  optimisticContacts: {},
  billingEventsSnapshot: null,
};

export const crmReducer = createReducer(
  initialState,
  on(
    CrmActions.loadContacts,
    CrmActions.loadContactDetail,
    CrmActions.loadMatters,
    CrmActions.loadMatterDetail,
    CrmActions.loadMatterTimeline,
    CrmActions.loadBillingEvents,
    CrmActions.createIntake,
    (state) => ({ ...state, isLoading: true, error: null })
  ),
  on(CrmActions.loadContactsSuccess, (state: CrmState, { contacts }) => ({
    ...state,
    contacts,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.loadContactDetailSuccess, (state: CrmState, { contact }) => ({
    ...state,
    currentContact: contact,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.loadMattersSuccess, (state: CrmState, { matters }) => ({
    ...state,
    matters,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.clearMatters, (state: CrmState) => ({
    ...state,
    matters: [],
  })),
  on(CrmActions.loadMatterDetailSuccess, (state: CrmState, { matter }) => ({
    ...state,
    currentMatter: matter,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.clearCurrentMatter, (state: CrmState) => ({
    ...state,
    currentMatter: null,
  })),
  on(CrmActions.loadMatterTimelineSuccess, (state: CrmState, { timeline, nextCursor }) => ({
    ...state,
    timeline,
    nextTimelineCursor: nextCursor,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.loadBillingEventsSuccess, (state: CrmState, { events, totalAmount }) => ({
    ...state,
    billingEvents: events,
    billingTotal: totalAmount,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.updateContactSuccess, (state: CrmState, { contact, contactId }) => {
    const id = (contact as any)?._id ?? contactId ?? null;
    return {
      ...state,
      contacts: id
        ? state.contacts.map((c: any) => (c._id === id ? { ...c, ...contact } : c))
        : state.contacts,
      currentContact:
        state.currentContact && state.currentContact._id === id
          ? { ...state.currentContact, ...contact }
          : state.currentContact,
      isLoading: false,
      error: null,
    };
  }),
  on(CrmActions.updateContactOptimistic, (state: CrmState, { contactId, contact }) => {
    const prev = state.contacts.find((c: any) => c._id === contactId) || state.currentContact || null;
    const alreadySnapshotted = !!(state.optimisticContacts || {})[contactId];
    return {
      ...state,
      // First-write-wins: only capture the snapshot before the FIRST optimistic
      // edit for this contact.  Overwriting on subsequent edits would lose the
      // original pre-mutation value and make rollback restore a half-edited state.
      optimisticContacts: {
        ...(state.optimisticContacts || {}),
        ...(!alreadySnapshotted && prev ? { [contactId]: prev } : {}),
      },
      contacts: state.contacts.map((c: any) => (c._id === contactId ? { ...c, ...contact } : c)),
      currentContact:
        state.currentContact && state.currentContact._id === contactId
          ? { ...state.currentContact, ...contact }
          : state.currentContact,
    };
  }),
  on(CrmActions.updateContactFailure, (state: CrmState, { error, contactId }) => {
    if (!contactId) {
      return { ...state, isLoading: false, error };
    }
    const prev = state.optimisticContacts ? state.optimisticContacts[contactId] : null;
    return {
      ...state,
      contacts: prev
        ? state.contacts.map((c: any) => (c._id === contactId ? prev : c))
        : state.contacts,
      currentContact:
        prev && state.currentContact && state.currentContact._id === contactId
          ? prev
          : state.currentContact,
      optimisticContacts: Object.keys(state.optimisticContacts || {})
        .filter((k) => k !== contactId)
        .reduce((acc: any, k) => ({ ...acc, [k]: (state.optimisticContacts || {})[k] }), {}),
      isLoading: false,
      error,
    };
  }),
  on(CrmActions.updateMatterSuccess, (state: CrmState, { matter, matterId }) => {
    const id = (matter as any)?._id ?? matterId ?? null;
    return {
      ...state,
      matters: id
        ? state.matters.map((existing: any) => (existing._id === id ? { ...existing, ...matter } : existing))
        : state.matters,
      currentMatter:
        state.currentMatter && state.currentMatter._id === id
          ? { ...state.currentMatter, ...matter }
          : state.currentMatter,
      isLoading: false,
      error: null,
    };
  }),
  on(CrmActions.addBillingEvent, (state: CrmState, { optimisticEvent }) => ({
    ...state,
    // Snapshot the pre-mutation billing state so failure can restore it exactly,
    // regardless of any other actions that may fire before the API responds.
    billingEventsSnapshot: { events: state.billingEvents, total: state.billingTotal },
    billingEvents: [optimisticEvent, ...state.billingEvents],
    billingTotal: state.billingTotal + (optimisticEvent.amount || 0),
    error: null,
  })),
  on(CrmActions.addBillingEventSuccess, (state: CrmState, { event }) => ({
    ...state,
    // Replace the temporary optimistic entry (identified by its unique temp- id)
    // with the confirmed server response, then clear the snapshot.
    billingEvents: state.billingEvents.map((existing) =>
      existing._id?.startsWith('temp-') && existing._id === event._id?.replace(/^temp-/, 'temp-')
        ? event
        : existing._id?.startsWith('temp-') &&
          existing.amount === event.amount &&
          existing.description === event.description
        ? event
        : existing
    ),
    billingEventsSnapshot: null,
  })),
  on(CrmActions.addBillingEventFailure, (state: CrmState, { error }) => {
    // Restore from snapshot if available — guarantees correct rollback even if
    // other actions modified billingEvents between the optimistic write and failure.
    if (state.billingEventsSnapshot) {
      return {
        ...state,
        billingEvents: state.billingEventsSnapshot.events,
        billingTotal: state.billingEventsSnapshot.total,
        billingEventsSnapshot: null,
        error,
      };
    }
    // Fallback: snapshot missing (should not happen in normal flow).
    return { ...state, billingEventsSnapshot: null, error };
  }),
  on(CrmActions.createIntakeSuccess, (state: CrmState, { contact, matter }) => ({
    ...state,
    contacts: [contact, ...state.contacts],
    matters: [matter, ...state.matters],
    currentContact: contact,
    currentMatter: matter,
    lastCreatedMatterId: matter._id || null,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.createContactSuccess, (state: CrmState, { contact }) => ({
    ...state,
    contacts: [contact, ...state.contacts],
    currentContact: contact,
    lastCreatedContactId: contact._id || null,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.createMatterSuccess, (state: CrmState, { matter }) => ({
    ...state,
    currentMatter: matter,
    lastCreatedMatterId: matter._id || null,
    isLoading: false,
    error: null,
  })),
  on(
    CrmActions.loadContactsFailure,
    CrmActions.loadContactDetailFailure,
    CrmActions.loadMattersFailure,
    CrmActions.loadMatterDetailFailure,
    CrmActions.loadMatterTimelineFailure,
    CrmActions.loadBillingEventsFailure,
    CrmActions.createIntakeFailure,
    CrmActions.updateMatterFailure,
    (state: CrmState, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })
  ),
  on(CrmActions.clearCrmError, (state: CrmState) => ({
    ...state,
    error: null,
  })),
  on(CrmActions.loadMatterDocuments, (state: CrmState) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(CrmActions.loadMatterDocumentsSuccess, (state: CrmState, { documents }) => ({
    ...state,
    documents,
    isLoading: false,
    error: null,
  })),
  on(CrmActions.loadMatterDocumentsFailure, (state: CrmState, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(CrmActions.uploadMatterDocument, (state: CrmState, { filename }) => ({
    ...state,
    uploadProgress: {
      isLoading: true,
      percentage: 0,
      filename,
      error: null,
      success: null,
    },
  })),
  on(CrmActions.uploadMatterDocumentSuccess, (state: CrmState, { document }) => ({
    ...state,
    documents: [document, ...state.documents],
    uploadProgress: {
      isLoading: false,
      percentage: 100,
      filename: document.filename,
      error: null,
      success: `${document.filename} uploaded successfully`,
    },
  })),
  on(CrmActions.uploadMatterDocumentFailure, (state: CrmState, { error }) => ({
    ...state,
    uploadProgress: {
      isLoading: false,
      percentage: 0,
      filename: state.uploadProgress.filename,
      error,
      success: null,
    },
  })),
  on(CrmActions.deleteMatterDocument, (state: CrmState) => ({
    ...state,
    isLoading: true,
  })),
  on(CrmActions.deleteMatterDocumentSuccess, (state: CrmState, { documentId }) => ({
    ...state,
    isLoading: false,
    documents: state.documents.filter((d: any) => d._id !== documentId && d.id !== documentId),
  })),
  on(CrmActions.deleteMatterDocumentFailure, (state: CrmState, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(CrmActions.clearUploadProgress, (state: CrmState) => ({
    ...state,
    uploadProgress: {
      isLoading: false,
      percentage: 0,
      filename: '',
      error: null,
      success: null,
    },
  }))
);
